/**
 * Post-checkout success page — polls the backend until Stripe reports
 * `payment_status = "paid"`, then bounces back to /institution/billing.
 */
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ status: "initiated", payment_status: "pending" });
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/stripe/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status !== "paid" && attemptsRef.current < 30) {
          setTimeout(tick, 2000);
        }
      } catch (e) {
        setError(e?.response?.data?.detail?.code || e.message);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = status.payment_status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="billing-success-page">
      <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 space-y-4">
        <div className="text-xs uppercase tracking-widest text-white/50">Billing</div>
        <h1 className="text-2xl font-semibold text-white" data-testid="billing-success-heading">
          {isPaid ? "Subscription active" : "Confirming your subscription…"}
        </h1>
        <p className="text-white/70 text-sm">
          {isPaid
            ? "Your VeraLok institutional plan is now active. Included verification quota has been provisioned."
            : "We're confirming your payment with Stripe. This usually takes a few seconds."}
        </p>
        {status.plan_id && (
          <div className="text-white/50 text-xs">
            Plan: <span className="text-white/80 font-medium" data-testid="billing-success-plan">{status.plan_id}</span>
          </div>
        )}
        {error && (
          <div className="text-rose-300 text-sm" data-testid="billing-success-error">{String(error)}</div>
        )}
        <div className="pt-2">
          <Link to="/institution/billing">
            <Button data-testid="billing-success-back">Back to Billing</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
